import { useCallback, useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';

const requestPerimeters = (idCompany, status) =>
  new Promise((resolve, reject) => {
    ApiDatabase.getAdminCompanyPerimeters(
      { idCompany, status },
      resolve,
      reject,
    );
  });

const asList = (value) => (Array.isArray(value) ? value : []);

const EMPTY = {
  status: 'loading',
  active: [],
  archived: [],
  assignableMembers: [],
  hasArchivedError: false,
};

// The perimeters of the panel's company, active and archived. Read once when
// the contacts tab opens and again after each perimeter gesture; a reload
// keeps the current lists on screen until the answer comes.
const useCompanyPerimeters = (idCompany) => {
  const [state, setState] = useState(EMPTY);
  // Requests for two companies do not cancel each other (their urls differ):
  // only the latest one may fill the block.
  const latestRequest = useRef(null);

  const load = useCallback(() => {
    if (!idCompany) return;
    const request = {};
    latestRequest.current = request;
    // The archived list is secondary: its failure must not hide the active
    // perimeters, so it resolves to null instead of rejecting.
    const archivedRequest = requestPerimeters(idCompany, 'archived').catch(
      (err) => {
        console.error(err);
        return null;
      },
    );
    Promise.all([requestPerimeters(idCompany, 'active'), archivedRequest])
      .then(([active, archived]) => {
        if (latestRequest.current !== request) return;
        setState({
          status: 'ready',
          active: asList(active?.data),
          archived: asList(archived?.data),
          assignableMembers: asList(active?.assignableMembers),
          hasArchivedError: archived === null,
        });
      })
      .catch((err) => {
        if (latestRequest.current !== request) return;
        console.error(err);
        setState({ ...EMPTY, status: 'error' });
      });
  }, [idCompany]);

  useEffect(() => {
    setState(EMPTY);
    load();
    return () => {
      latestRequest.current = null;
    };
  }, [load]);

  const retry = () => {
    setState(EMPTY);
    load();
  };

  return { ...state, reload: load, retry };
};

export default useCompanyPerimeters;
